/**
 * Shapes for managing users on a CLIENT INSTANCE from HQ — mirrors of the API's
 * `instance-connections`, `instance-users` and `instance-remote-audit`
 * (packages/api/src/services/infrastructure/). Keep them in step by hand.
 */

export type InstanceEnvironment = "prod" | "impl" | "dev";

export const INSTANCE_ENVIRONMENTS: { value: InstanceEnvironment; label: string }[] = [
  { value: "prod", label: "Production" },
  { value: "impl", label: "Implementation" },
  { value: "dev", label: "Development" },
];

export const environmentLabel = (env: string): string =>
  INSTANCE_ENVIRONMENTS.find((e) => e.value === env)?.label ?? env;

export type InstanceConnectionStatus = "no-key" | "unverified" | "verified" | "invalid";

/** The entry an operator puts in the instance's `authentication.apiKey.keys[]`. */
export interface InstanceKeyConfig {
  id: string;
  label: string;
  sha256: string;
  userEmail: string;
  paths: string[];
  methods: string[];
}

export interface InstanceConnection {
  id: string;
  clientId: string;
  environment: InstanceEnvironment;
  apiBaseUrl: string;
  /** Where the client's people open their IntraSync app (#17); null until an administrator enters it. */
  webBaseUrl?: string | null;
  appName: string | null;
  serviceUserEmail: string;
  keyId: string | null;
  hasApiKey: boolean;
  keyIssuedAt: string | null;
  /**
   * HQ holds the instance's inquiry publishing key (its clientInquiries.inboundSecret),
   * so it can put a released client question into this client's app (Decision 5,
   * 2026-11-02). The key itself is never sent to the browser. Absent from an API
   * older than this field.
   */
  hasInquiryKey?: boolean;
  inquiryKeySetAt?: string | null;
  status: InstanceConnectionStatus | string;
  lastValidatedAt: string | null;
  lastValidationMessage: string | null;
  /** Only on a generate-key response. */
  keyConfig?: InstanceKeyConfig;
  /** Only on a validate response. */
  validation?: { ok: boolean; status: string; message?: string };
}

export interface InstanceConnectionFormData {
  environment: InstanceEnvironment;
  apiBaseUrl: string;
  /** The app's address; blank when unknown. */
  webBaseUrl: string;
  appName: string;
  serviceUserEmail: string;
}

export interface InstanceUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string | null;
  departmentId: string | null;
  team: string | null;
  teamId: string | null;
  isValidated: boolean;
  adminUser: boolean;
  lastLoginDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface InstanceUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  team: string;
  /** Add only: an existing user on the same instance to copy page permissions from. Roles are never copied. */
  copyAccessFromUserId: string;
}

export interface AccessCopyResult {
  ok: boolean;
  permissions: number;
  message?: string;
}

export interface InstanceUserWriteResult {
  user: InstanceUser;
  temporaryPassword?: string;
  accessCopy?: AccessCopyResult;
}

export interface InstanceRemoteAuditRow {
  id: string;
  connectionId: string;
  clientId: string;
  environment: string;
  action: string;
  targetId: string | null;
  targetLabel: string | null;
  outcome: "ok" | "partial" | "error" | string;
  httpStatus: number | null;
  actorUserId: number | null;
  actorName: string | null;
  detail: string | null;
  createdAt: string;
}

/** A one-time secret shown to the operator who asked for it, then forgotten. */
export interface OneTimeSecret {
  kind: "new-user" | "reset-password";
  email: string;
  password: string;
  accessCopy?: AccessCopyResult;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  "connection.generate-key": "Issued a new key",
  "connection.set-inquiry-key": "Entered the inquiry publishing key",
  "connection.clear-inquiry-key": "Removed the inquiry publishing key",
  "inquiry.publish": "Put a client question into their app",
  "user.create": "Added user",
  "user.update": "Edited user",
  "user.disable": "Disabled user",
  "user.enable": "Re-enabled user",
  "user.reset-password": "Reset password",
  "user.copy-access": "Copied access",
};

export const auditActionLabel = (action: string): string => AUDIT_ACTION_LABELS[action] ?? action;

/**
 * What the Users tab says about a connection's state, and whether user
 * management can be attempted at all. Honest about the three reasons it cannot:
 * no connection, no key, or a key the instance refused.
 */
export function connectionReadiness(connection: InstanceConnection | null): {
  usable: boolean;
  severity: "success" | "info" | "warning" | "error";
  message: string;
} {
  if (!connection) {
    return {
      usable: false,
      severity: "info",
      message: "No connection to this client's instance yet. An admin sets one up under Connection settings.",
    };
  }
  if (!connection.hasApiKey) {
    return {
      usable: false,
      severity: "warning",
      message: "This connection has no key yet. An admin issues one under Connection settings and configures it on the instance.",
    };
  }
  if (connection.status === "invalid") {
    return {
      usable: true,
      severity: "error",
      message: `The instance refused HQ's last check${
        connection.lastValidationMessage ? `: ${connection.lastValidationMessage}` : "."
      } The instance may not have adopted the core release that accepts HQ's key yet.`,
    };
  }
  if (connection.status === "verified") {
    return { usable: true, severity: "success", message: "Connected." };
  }
  return {
    usable: true,
    severity: "info",
    message: "Key issued but not checked yet. Run Check connection once it is configured on the instance.",
  };
}

/**
 * An instance user's last login. Core stores it as a bare DATE, and
 * `new Date("2026-09-24")` is UTC midnight — a local clock shows it as a time
 * that never happened ("12:00:00 AM", or the previous evening west of UTC). A
 * date-only value is shown as that calendar date; a full timestamp as local time.
 */
export function formatLastLogin(value: string | null | undefined): string {
  if (!value) return "Never";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    if (value === "1970-01-01") return "Never"; // core's schema default
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

/** Core refuses to start with a shorter clientInquiries.inboundSecret (core-api MIN_INBOUND_SECRET_LENGTH); the API refuses it too. */
export const MIN_INQUIRY_KEY_LENGTH = 32;
/** What an HTTP header comfortably carries; core itself sets no maximum. The API's limit. */
export const MAX_INQUIRY_KEY_LENGTH = 4096;

/**
 * Why a pasted inquiry publishing key cannot be the instance's, or null. Says the
 * shape, never the value. The API's rule (instance-keys.ts `parseInquiryKey`): what
 * core refuses, or what an HTTP header cannot carry — nothing stricter.
 */
export function inquiryKeyProblem(raw: string): string | null {
  const key = raw.trim();
  if (!key) return null;
  if (key.length < MIN_INQUIRY_KEY_LENGTH) {
    return `At least ${MIN_INQUIRY_KEY_LENGTH} characters: the instance refuses to start with a shorter one.`;
  }
  if (key.length > MAX_INQUIRY_KEY_LENGTH) return `At most ${MAX_INQUIRY_KEY_LENGTH} characters: paste only the key.`;
  for (let i = 0; i < key.length; i++) {
    // A control character (a line break included) or one beyond Latin-1: a header cannot carry it.
    const c = key.charCodeAt(i);
    if ((c < 0x20 && c !== 0x09) || c === 0x7f || c > 0xff) {
      return "Paste only the key: it has a line break or a character an HTTP header cannot carry.";
    }
  }
  return null;
}

/** The key entry exactly as it goes into the instance's config, pretty-printed. */
export const formatKeyConfig = (config: InstanceKeyConfig): string => JSON.stringify(config, null, 2);
